import { useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useDesignerPermissionGate } from '../hooks/useDesignerPermissionGate';

interface DesignerPermissionGateProps {
  children: ReactNode;
}

/**
 * Route-level permission gate for the Designer page.
 *
 * Wraps both native DesignerPage and Scio1Frame paths to enforce
 * folder security regardless of the VITE_USE_NATIVE_DESIGNER flag.
 *
 * Renders children immediately (no blocking spinner) to avoid breaking
 * Scio1Frame initialization. If the permission check fails, redirects
 * the user away before the Designer iframe finishes loading.
 */
export function DesignerPermissionGate({ children }: DesignerPermissionGateProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { isAllowed, isLoading } = useDesignerPermissionGate();

  // Redirect if not allowed after permission check completes.
  // Children are rendered immediately to avoid breaking Scio1Frame init,
  // but the redirect fires before the Designer iframe finishes loading.
  useEffect(() => {
    if (!isLoading && !isAllowed) {
      toast.error(t('You do not have permission to edit this asset'));
      navigate('/app/assetManagement', { replace: true });
    }
  }, [isLoading, isAllowed, navigate, t]);

  return <>{children}</>;
}
